import React, { useEffect, useMemo } from 'react';
import { EntityListResponse, EntityListSourceType } from '@ydistri/api-sdk';
import { Column, ReadOnlyItem, Row } from '@ydistri/ds';
import AdministrationItemCalculationsUsageComponent from '../../common/usage/AdministrationItemCalculationsUsageComponent';
import { useGetEntityListCalculationsQuery, useGetEntityListHoldersQuery } from '../apiEntityLists';
import EntityListArchivationInfo from './EntityListArchivationInfo';
import EntityListHolders from './EntityListHolders';
import { EntityListDescription } from '../../common/EntityListDescription';
import { SYSTEM_ID_LABEL } from '../../../../components/global/_constants';
import { useClipboardHandlers } from '../../../../lib/hooks/useClipboardHandlers';
import { flatten, stringSorter } from '@ydistri/generic-utils';

interface EntityListDetailsProps {
  entityList: EntityListResponse;
}

/** Common width for the labels to have the data aligned */
const LABEL_WIDTH = '12rem';

interface EntityListID {
  label: string;
  id: string;
}

/**
 * Details of a list like title, id etc.
 * @param targetList
 * @constructor
 */
const EntityListDetails: React.FC<EntityListDetailsProps> = ({ entityList }) => {
  const {
    data: entityListCalculations = [],
    isFetching,
    refetch: refetchEntityListCalculations,
  } = useGetEntityListCalculationsQuery(entityList.entityListId);
  const holders = useGetEntityListHoldersQuery(entityList.entityListId);
  const refetchHolders = holders.refetch;
  const clipboardHandlers = useClipboardHandlers();

  useEffect(() => {
    refetchEntityListCalculations();
    refetchHolders();
  }, [refetchEntityListCalculations, refetchHolders, entityList]);

  const entityListTypeNames = flatten(
    entityList.entityListTypes.map((type): string => type.entityListTypeName).sort(stringSorter),
  );

  const idList = useMemo(() => {
    const ids: EntityListID[] = [];

    if (entityList.customerEntityListId && entityList.customerEntityListId !== '') {
      ids.push({
        label: 'Id',
        id: entityList.customerEntityListId,
      });
    }

    ids.push({
      label: SYSTEM_ID_LABEL,
      id: entityList.entityListId.toString(),
    });

    ids.push({
      label: 'Version Id',
      id: entityList.lastEntityListVersionId.toString(),
    });

    return ids;
  }, [
    entityList.customerEntityListId,
    entityList.entityListId,
    entityList.lastEntityListVersionId,
  ]);

  return (
    <Row $gap="3rem" data-type="EntityListDetails">
      <Column $gap="1rem">
        {entityList.description && (
          <ReadOnlyItem
            label="Description"
            labelWidth={LABEL_WIDTH}
            alignItems="flex-start"
            value={<EntityListDescription>{entityList.description}</EntityListDescription>}
          />
        )}
        {entityList.entityListSourceTypeId === EntityListSourceType.User && (
          <EntityListArchivationInfo
            archived={entityList.isArchived}
            archivedAt={entityList.archivedAt}
            scheduledArchivedOn={entityList.scheduledArchiveOn}
            labelWidth={LABEL_WIDTH}
          />
        )}
        <Row $gap="1rem">
          {idList.map((idItem, index) => {
            return (
              <ReadOnlyItem
                key={idItem.id}
                label={idItem.label}
                value={idItem.id}
                labelWidth={index === 0 ? LABEL_WIDTH : undefined}
                copyOnClick={true}
                {...clipboardHandlers}
              />
            );
          })}
        </Row>

        <AdministrationItemCalculationsUsageComponent
          calculations={entityListCalculations}
          fetching={isFetching}
          labelWidth={LABEL_WIDTH}
        />
        <EntityListHolders
          holders={holders.currentData}
          isDefault={entityList.isDefault}
          isError={holders.isError}
          isFetching={holders.isFetching}
          labelWidth={LABEL_WIDTH}
        />
      </Column>
      <Column $gap="1rem">
        <ReadOnlyItem
          label="Type"
          labelWidth={LABEL_WIDTH}
          value={entityListTypeNames}
          data-type="EntityListTypeNames"
        />
        <ReadOnlyItem
          label="Source type"
          value={entityList.entityListSourceTypeId}
          labelWidth={LABEL_WIDTH}
          data-type="EntityListSourceType"
        />
      </Column>
    </Row>
  );
};

export default EntityListDetails;
